import * as React from 'react'
import { ChevronLeftIcon, ChevronRightIcon, MoreHorizontalIcon, Loader2 } from 'lucide-react'

import { cn } from '../../lib/utils'
import { type Button, buttonVariants } from './button'

function Pagination({ className, ...props }: React.ComponentProps<'nav'>) {
  return (
    <nav
      role="navigation"
      aria-label="pagination"
      data-slot="pagination"
      className={cn('mx-auto flex w-full justify-center', className)}
      {...props}
    />
  )
}

function PaginationContent({ className, ...props }: React.ComponentProps<'ul'>) {
  return (
    <ul
      data-slot="pagination-content"
      className={cn('flex flex-row items-center gap-1', className)}
      {...props}
    />
  )
}

function PaginationItem({ ...props }: React.ComponentProps<'li'>) {
  return <li data-slot="pagination-item" {...props} />
}

type PaginationLinkProps = {
  isActive?: boolean
} & Pick<React.ComponentProps<typeof Button>, 'size'> &
  React.ComponentProps<'a'>

function PaginationLink({ className, isActive, size = 'icon', ...props }: PaginationLinkProps) {
  return (
    <a
      aria-current={isActive ? 'page' : undefined}
      data-slot="pagination-link"
      data-active={isActive}
      className={cn(
        buttonVariants({
          variant: isActive ? 'outline' : 'ghost',
          size,
        }),
        className,
      )}
      {...props}
    />
  )
}

function PaginationPrevious({ className, ...props }: React.ComponentProps<typeof PaginationLink>) {
  return (
    <PaginationLink
      aria-label="Go to previous page"
      size="default"
      className={cn('gap-1 px-2.5 sm:pl-2.5', className)}
      {...props}
    >
      <ChevronLeftIcon />
      <span className="hidden sm:block">Previous</span>
    </PaginationLink>
  )
}

function PaginationNext({ className, ...props }: React.ComponentProps<typeof PaginationLink>) {
  return (
    <PaginationLink
      aria-label="Go to next page"
      size="default"
      className={cn('gap-1 px-2.5 sm:pr-2.5', className)}
      {...props}
    >
      <span className="hidden sm:block">Next</span>
      <ChevronRightIcon />
    </PaginationLink>
  )
}

function PaginationEllipsis({ className, ...props }: React.ComponentProps<'span'>) {
  return (
    <span
      aria-hidden
      data-slot="pagination-ellipsis"
      className={cn('flex size-9 items-center justify-center', className)}
      {...props}
    >
      <MoreHorizontalIcon className="size-4" />
      <span className="sr-only">More pages</span>
    </span>
  )
}

// Cursor-based pagination types and interfaces
export interface PageInfo {
  hasNextPage: boolean
  hasPreviousPage: boolean
  startCursor?: string | null
  endCursor?: string | null
}

export interface CursorPaginationProps {
  pageInfo: PageInfo
  totalCount: number
  loading?: boolean
  onNext: (cursor: string) => void
  onPrevious: (cursor: string) => void
}

// Cursor-based pagination component
export function CursorPagination({
  pageInfo,
  totalCount,
  loading = false,
  onNext,
  onPrevious,
}: CursorPaginationProps) {
  const handleNext = () => {
    if (pageInfo.hasNextPage && pageInfo.endCursor) {
      onNext(pageInfo.endCursor)
    }
  }

  const handlePrevious = () => {
    if (pageInfo.hasPreviousPage && pageInfo.startCursor) {
      onPrevious(pageInfo.startCursor)
    }
  }

  return (
    <div className="flex items-center justify-between space-x-4">
      <div className="text-sm text-muted-foreground">
        Total: {totalCount.toLocaleString()} items
      </div>

      <div className="flex items-center space-x-2">
        <button
          onClick={handlePrevious}
          disabled={!pageInfo.hasPreviousPage || loading}
          className={cn(
            buttonVariants({ variant: 'outline', size: 'sm' }),
            'gap-1',
            (!pageInfo.hasPreviousPage || loading) && 'opacity-50 cursor-not-allowed',
          )}
        >
          {loading ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <ChevronLeftIcon className="h-4 w-4" />
          )}
          Previous
        </button>

        <button
          onClick={handleNext}
          disabled={!pageInfo.hasNextPage || loading}
          className={cn(
            buttonVariants({ variant: 'outline', size: 'sm' }),
            'gap-1',
            (!pageInfo.hasNextPage || loading) && 'opacity-50 cursor-not-allowed',
          )}
        >
          Next
          {loading ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <ChevronRightIcon className="h-4 w-4" />
          )}
        </button>
      </div>
    </div>
  )
}

export {
  Pagination,
  PaginationContent,
  PaginationLink,
  PaginationItem,
  PaginationPrevious,
  PaginationNext,
  PaginationEllipsis,
}
